import { InfiniteData, useMutation, useQueryClient } from '@tanstack/react-query';
import { client } from '@/services/api/axios.ts';
import { NotificationResponse } from '@/features/notifications/hooks/queries/get-all-notifications.ts';
import { NotificationStatus } from '@/features/notifications/schemas/notifications.schemas';
import { useNotificationsStore } from '@/services/store/use-notifications-store.ts';

export const useUpdateNotificationsQuery = () => {
  const notificationFilter = useNotificationsStore((state) => state.notificationFilter);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      notificationIds,
      status,
    }: {
      notificationIds: string[];
      status: NotificationStatus;
    }) => {
      const { data } = await client.patch(`/notifications`, {
        notificationIds,
        status,
      });
      return data;
    },

    onMutate: async ({
      notificationIds,
      status,
    }: {
      notificationIds: string[];
      status: NotificationStatus;
    }) => {
      await queryClient.cancelQueries({ queryKey: ['notifications', notificationFilter] });

      const previousData = queryClient.getQueryData<
        InfiniteData<NotificationResponse> | undefined
      >(['notifications', notificationFilter]);

      queryClient.setQueryData<InfiniteData<NotificationResponse>>(
        ['notifications', notificationFilter],
        (previousInfiniteQueryData: InfiniteData<NotificationResponse> | undefined) => {
          if (!previousInfiniteQueryData) return previousInfiniteQueryData;
          return {
            ...previousInfiniteQueryData,
            pages:
              previousData?.pages?.map((page) => {
                const newBody = page.body.map((post) => {
                  if (post?.notificationId && !notificationIds.includes(post?.notificationId))
                    return post;

                  return {
                    ...post,
                    status,
                  };
                });

                return {
                  ...page,
                  body: newBody,
                  length: newBody.length,
                };
              }) ?? [],
          };
        }
      );

      return { previousData };
    },

    onError: (_, __, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(['notifications', notificationFilter], context.previousData);
      }
    },

    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });
};
